import React, { useMemo } from 'react';
import {
  View,
  ActivityIndicator,
  Text,
  Modal,
  ViewStyle,
  TextStyle,
  ViewProps,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { loaderOverlayStyles } from '../../../../shared/components/feedback/LoaderOverlay/LoaderOverlay.styles';
import { palette } from '@shared/theme/colorGroups/palette.colors';

interface LoaderOverlayProps extends ViewProps {
  visible: boolean;
  message?: string;
  containerStyle?: ViewStyle;
  messageStyle?: TextStyle;
  /**
   * When `true` (default) the overlay is presented inside a `<Modal>` —
   * suitable for root-level usage (e.g. App.tsx).
   *
   * When `false` the overlay is rendered as an absolute-fill `<View>`
   * inside the current component tree. Use this when LoaderOverlay is
   * rendered *inside* another `<Modal>` to avoid the iOS "slide-up"
   * animation that appears whenever a second `<Modal>` is stacked on top
   * of an already-open one.
   */
  isModal?: boolean;
  /**
   * When `true` the overlay is rendered as an absolute-fill `<View>`
   * positioned inside the parent modal. Use this instead of `isModal={false}`
   * for a more descriptive API when placing the loader inside another modal.
   * Takes precedence over `isModal` when set to `true`.
   */
  insideModal?: boolean;
}

const LoaderOverlay = ({
  visible,
  message,
  containerStyle,
  messageStyle,
  isModal = true,
  insideModal = false,
  ...rest
}: LoaderOverlayProps) => {
  const styles = useMemo(() => loaderOverlayStyles(), []);
  const { t } = useTranslation();

  /** Shared spinner + optional message content */
  const loaderContent = (
    <View {...rest} style={[styles.container, containerStyle]}>
      <View
        style={styles.loaderBox}
        accessible={true}
        accessibilityLiveRegion="assertive"
        accessibilityLabel={message || t('accessibility.loadingMessage')}
      >
        <ActivityIndicator
          size="large"
          color={palette.blue.primary}
          accessibilityElementsHidden={true}
        />
        {message && (
          <Text style={[styles.message, messageStyle]}>{message}</Text>
        )}
      </View>
    </View>
  );

  // ── insideModal mode ─────────────────────────────────────────────────────
  // Renders as an absolute-fill <View> scoped to the parent modal's bounds.
  // Use this when LoaderOverlay is placed *inside* another <Modal> to avoid
  // stacking a second <Modal> (which causes the iOS slide-up animation and
  // touch-freeze). Returns null when not visible so it unmounts cleanly.
  if (insideModal) {
    if (!visible) {
      return null;
    }
    return (
      <View
        style={styles.overlay}
        pointerEvents="box-only"
        accessibilityViewIsModal={true}>
        {loaderContent}
      </View>
    );
  }

  // ── Inline View mode (isModal={false}) ───────────────────────────────────
  // Renders as a plain absolute-fill <View> in the component tree (not inside
  // another Modal). Suitable for full-screen loading states where a second
  // <Modal> is undesirable. Returns null when not visible.
  if (!isModal) {
    if (!visible) {
      return null;
    }
    return (
      <View
        style={styles.overlay}
        pointerEvents="box-only"
        accessibilityViewIsModal={true}>
        {loaderContent}
      </View>
    );
  }

  // ── Default Modal mode (isModal={true}) ──────────────────────────────────
  // Renders via React Native's <Modal> — suitable for root-level usage
  // (e.g. App.tsx / index.web.tsx) where a full-screen overlay is needed.
  return (
    <Modal
      transparent
      animationType="none"
      supportedOrientations={[
        'portrait',
        'landscape',
        'landscape-left',
        'landscape-right',
      ]}
      visible={visible}
      statusBarTranslucent
      accessibilityViewIsModal={true}
    >
      {loaderContent}
    </Modal>
  );
};

export default LoaderOverlay;
